import { useVirtualizer } from '@tanstack/react-virtual';
import { useRef } from 'react';
import styled from 'styled-components';

import { MenuCheckboxItem } from '@creditornot/cb-menu';
import { Empty } from '@creditornot/cb-placeholders';
import { useFormatMessage } from 'common/hooks/useFormatMessage';

import { ThinScrollbarContainer } from '../../ThinScrollbarContainer';

const ScrollContainer = styled(ThinScrollbarContainer)`
  max-height: 400px;
`;

const ItemList = styled.div<{ $height: number }>`
  display: flex;
  flex-direction: column;
  height: ${({ $height }) => $height}px;
  position: relative;
  width: 100%;
`;

const StyledMenuCheckboxItem = styled(MenuCheckboxItem)`
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
`;

const StyledEmpty = styled(Empty)`
  margin: 16px 0;
`;

type Props = {
  brands: {
    id: string;
    name: string;
  }[];
  onClick: (id: string) => void;
  selectedBrandIds: string[];
};

export function BrandsFilterList({ brands, onClick, selectedBrandIds }: Props) {
  const f = useFormatMessage();
  const parentRef = useRef(null);
  const virtualizer = useVirtualizer({
    count: brands?.length || 0,
    estimateSize: () => 28,
    gap: 8,
    getScrollElement: () => parentRef.current,
    overscan: 10,
  });

  return (
    <ScrollContainer ref={parentRef}>
      <ItemList $height={virtualizer.getTotalSize()} role="menu">
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const { id, name } = brands[virtualItem.index];
          return (
            <StyledMenuCheckboxItem
              checked={selectedBrandIds.includes(id)}
              data-index={virtualItem.index}
              key={virtualItem.key}
              onClick={() => onClick(id)}
              ref={virtualizer.measureElement}
              style={{
                height: `${virtualItem.size}px`,
                transform: `translateY(${virtualItem.start}px)`,
              }}
              value={id}
            >
              {name}
            </StyledMenuCheckboxItem>
          );
        })}
      </ItemList>
      {brands.length === 0 && (
        <StyledEmpty description={f('components.brands_filter.no_brands_found')} />
      )}
    </ScrollContainer>
  );
}
